import { appConfig } from '@config/app.config';
import { ServiceRegistry, ServiceTokens } from './ServiceRegistry';
import { EventBus, GameEvents } from '../events/EventBus';
import { LocalizationService } from '@localization/LocalizationService';
import { SaveManager } from '@services/save/SaveManager';
import { AudioManager } from '@services/audio/AudioManager';
import { ApiManager } from '@services/api/ApiManager';
import { LeaderboardManager } from '@services/leaderboard/LeaderboardManager';
import { AdManager } from '@services/ads/AdManager';
import { GameManager } from '@gameplay/GameManager';
import { UIManager } from '@ui/UIManager';

/**
 * Uygulama giriş noktası — servis kaydı ve başlatma.
 * Phase 1: altyapı iskeleti.
 */
export class GameBootstrap {
  readonly registry = new ServiceRegistry();
  readonly eventBus = new EventBus();

  private started = false;

  async start(): Promise<void> {
    if (this.started) {
      return;
    }

    this.registerServices();
    await this.initializeServices();
    this.eventBus.publish({ type: GameEvents.appReady });
    this.started = true;
  }

  async stop(): Promise<void> {
    if (!this.started) {
      return;
    }

    await this.shutdownServices();
    this.registry.clear();
    this.eventBus.clear();
    this.started = false;
  }

  private registerServices(): void {
    this.registry.register(ServiceTokens.eventBus, this.eventBus);
    this.registry.register(ServiceTokens.localization, new LocalizationService());
    this.registry.register(ServiceTokens.saveManager, new SaveManager());
    this.registry.register(ServiceTokens.audioManager, new AudioManager());
    this.registry.register(ServiceTokens.apiManager, new ApiManager());
    this.registry.register(ServiceTokens.leaderboardManager, new LeaderboardManager());
    this.registry.register(ServiceTokens.adManager, new AdManager());
    this.registry.register(ServiceTokens.gameManager, new GameManager(this.eventBus));
    this.registry.register(ServiceTokens.uiManager, new UIManager());
  }

  private async initializeServices(): Promise<void> {
    const localization = this.registry.get<LocalizationService>(ServiceTokens.localization);
    await localization.setLocale(appConfig.defaultLocale);

    const managers = [
      this.registry.get<SaveManager>(ServiceTokens.saveManager),
      this.registry.get<AudioManager>(ServiceTokens.audioManager),
      this.registry.get<ApiManager>(ServiceTokens.apiManager),
      this.registry.get<LeaderboardManager>(ServiceTokens.leaderboardManager),
      this.registry.get<AdManager>(ServiceTokens.adManager),
      this.registry.get<GameManager>(ServiceTokens.gameManager),
      this.registry.get<UIManager>(ServiceTokens.uiManager),
      localization,
    ];

    for (const manager of managers) {
      await manager.initialize();
    }
  }

  private async shutdownServices(): Promise<void> {
    const managers = [
      this.registry.get<UIManager>(ServiceTokens.uiManager),
      this.registry.get<GameManager>(ServiceTokens.gameManager),
      this.registry.get<AdManager>(ServiceTokens.adManager),
      this.registry.get<LeaderboardManager>(ServiceTokens.leaderboardManager),
      this.registry.get<ApiManager>(ServiceTokens.apiManager),
      this.registry.get<AudioManager>(ServiceTokens.audioManager),
      this.registry.get<SaveManager>(ServiceTokens.saveManager),
      this.registry.get<LocalizationService>(ServiceTokens.localization),
    ];

    for (const manager of managers) {
      await manager.shutdown();
    }
  }
}

/** Global bootstrap instance — Phaser scene'ler tarafından erişilir */
export let bootstrap: GameBootstrap | undefined;

export function setBootstrap(instance: GameBootstrap): void {
  bootstrap = instance;
}

export function getBootstrap(): GameBootstrap {
  if (!bootstrap) {
    throw new Error('GameBootstrap is not initialized.');
  }
  return bootstrap;
}
